import { useRef } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import type { DocAssetType } from "@/lib/document/assetTypes";
import { DocumentAssetGrid } from "./DocumentAssetGrid";
import { DOC_ASSET_ACCEPT, DOC_ASSET_TYPE_OPTIONS } from "./docAssetTypeLabels";
import { useDocumentAssets } from "./useDocumentAssets";

type Props = {
  /** Administrator+ may upload; editors see read-only library. */
  canUpload?: boolean;
};

export function DocumentAssetLibraryPanel({ canUpload = true }: Props) {
  const assets = useDocumentAssets(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function onFileChange(file: File | null) {
    if (!file) return;
    void assets.submitUpload(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "doc-asset-library" }} className="space-y-4">
      <SectionCard
        title="Asset library"
        description={
          canUpload
            ? "Upload reusable images (logos, signatures, photos) for document blocks."
            : "Browse org assets for image blocks. Upload requires administrator access."
        }
      >
        <div className="mb-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => assets.setAssetTypeFilter("all")}
            className={filterBtnClass(assets.assetTypeFilter === "all")}
          >
            All
          </button>
          {DOC_ASSET_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => assets.setAssetTypeFilter(opt.id)}
              className={filterBtnClass(assets.assetTypeFilter === opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {canUpload ? (
          <div className="mb-4 grid gap-3 rounded-lg border border-gray-100 bg-gray-50/80 p-3 dark:border-zinc-700 dark:bg-zinc-800/40 sm:grid-cols-[1fr_auto_auto] sm:items-end">
            <label className="block space-y-1 text-sm">
              <span className="font-medium text-gray-700 dark:text-zinc-300">Display name</span>
              <input
                type="text"
                value={assets.uploadDisplayName}
                onChange={(e) => assets.setUploadDisplayName(e.target.value)}
                placeholder="Defaults to filename"
                className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
              />
            </label>
            <label className="block space-y-1 text-sm">
              <span className="font-medium text-gray-700 dark:text-zinc-300">Type</span>
              <select
                value={assets.uploadAssetType}
                onChange={(e) => assets.setUploadAssetType(e.target.value as DocAssetType)}
                className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
              >
                {DOC_ASSET_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex flex-col gap-2 sm:items-end">
              <input
                ref={fileInputRef}
                type="file"
                accept={DOC_ASSET_ACCEPT}
                className="hidden"
                onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
              />
              <button
                type="button"
                disabled={assets.uploadBusy}
                onClick={() => fileInputRef.current?.click()}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                {assets.uploadBusy ? "Uploading…" : "Upload image"}
              </button>
            </div>
          </div>
        ) : null}

        {assets.banner ? (
          <p className="mb-3 text-sm text-green-700 dark:text-green-300">{assets.banner}</p>
        ) : null}
        {assets.error ? (
          <p className="mb-3 text-sm text-red-700 dark:text-red-300">{assets.error}</p>
        ) : null}

        <DocumentAssetGrid
          assets={assets.assets}
          selectedAssetId={assets.selectedAssetId}
          onSelect={assets.setSelectedAssetId}
          loading={assets.loading}
        />

        {assets.selectedAsset ? (
          <div className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm dark:border-indigo-900/40 dark:bg-indigo-950/30">
            <p className="font-medium text-indigo-900 dark:text-indigo-100">
              Selected for image blocks: {assets.selectedAsset.display_name}
            </p>
            <p className="mt-1 font-mono text-xs text-indigo-800/80 dark:text-indigo-200/80">
              asset_id: {assets.selectedAsset.id}
            </p>
          </div>
        ) : null}
      </SectionCard>
    </div>
  );
}

function filterBtnClass(active: boolean): string {
  return [
    "rounded-full px-3 py-1 text-xs font-medium transition",
    active
      ? "bg-indigo-600 text-white dark:bg-indigo-500"
      : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700",
  ].join(" ");
}
